import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/3f757860/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import { StructuredListWrapper, StructuredListHead, StructuredListBody, StructuredListRow, StructuredListInput, StructuredListCell, UnorderedList, ListItem } from 'carbon-components-react';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const Caption = makeShortcode("Caption");
const Title = makeShortcode("Title");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <PageDescription mdxType="PageDescription">
      <p>{`Empty states are moments in an app where there is no data to display to the user. They are most commonly seen the first time a user interacts with a product or page, but can be used when data has been deleted or is unavailable.`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
  <AnchorLink mdxType="AnchorLink">Overview</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Anatomy</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Designing with empty states</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Types of empty states</AnchorLink>
  <AnchorLink mdxType="AnchorLink">In-depth alternatives</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Accessibility</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Related</AnchorLink>
  <AnchorLink mdxType="AnchorLink">References</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Feedback</AnchorLink>
    </AnchorLinks>
    <h2>{`Overview`}</h2>
    <p>{`Empty states are a simple yet extremely powerful way to keep a user informed, supported, and on a productive path. They provide opportunities to communicate what the user would see if they had data, while providing constructive guidance about next steps.`}</p>
    <p>{`With just enough contextual guidance, empty states ensure a smoothness of experience, especially when things aren’t working as expected.`}</p>
    <p>{`Most people are familiar with the basic empty state page that explains what data would normally appear on a page. However, it’s not always a one size fits all.`}</p>
    <p>{`This pattern explores the following approaches:`}</p>
    <ul>
      <li parentName="ul">{`Basic empty states for first use, user action confirmation, and error management`}</li>
      <li parentName="ul">{`In-depth supplements and alternatives for first use empty states, including in-line documentation, onboarding, and starter content`}</li>
    </ul>
    <h2>{`Anatomy`}</h2>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABZUlEQVQ4y62TPW/CMBCG81MRiIW1Mz8CiSFLgYq/0KGq1LFTO1VqK1UqNAEaAiS2Y8fhK3l7NgXRDwQDkR6dHZ0f3Z0TB2d+nOVyiXq9jlKphGq1ikqlgnK5fDIm35yt1WoIggBOnufodrtoNBpoNptwXRedTgftdvskTG6r1bIOzjmcs7dcFAWEENZuImMMMcG+sWtOCI44jg9ictfr9UaotUYYhvA9H5IJiIghifkGTvtgCtb/hEwVZJJASvkvP4SjUYD+BwmFghJ0iDCyhKKYROCD0AoTEia/pNv9TpimGtOxj9B/RjR8h9frI/CG4OMpEqrWVBc99SCz9LgwzwusFgrXDwoXVxrujQKbUUWEbZ2q5KMJ4rcBpD5RuMgU7l9SXN5q3D1SkpkdycSMhIzbduNXbyc8OkMpFeZaACuBfG7mts9mloLE2+oOsRNmWQZFc5RK22gu6Q/Zgfd7mJ/k7B/2F22VutrpZcAIAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Basic empty state with primary action button",
            "title": "Basic empty state with primary action button",
            "src": "/static/aa0a421fa893262bc6c0d9b45a66a040/3cbba/1_Empty_state_annotation_fullpage_website_image_1120.png",
            "srcSet": ["/static/aa0a421fa893262bc6c0d9b45a66a040/7fc1e/1_Empty_state_annotation_fullpage_website_image_1120.png 288w", "/static/aa0a421fa893262bc6c0d9b45a66a040/a5df1/1_Empty_state_annotation_fullpage_website_image_1120.png 576w", "/static/aa0a421fa893262bc6c0d9b45a66a040/3cbba/1_Empty_state_annotation_fullpage_website_image_1120.png 1152w", "/static/aa0a421fa893262bc6c0d9b45a66a040/0b124/1_Empty_state_annotation_fullpage_website_image_1120.png 1728w", "/static/aa0a421fa893262bc6c0d9b45a66a040/99571/1_Empty_state_annotation_fullpage_website_image_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Basic empty state with primary action button</Caption>
      </Column>
    </Row>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Image (optional):`}</strong>{` A non-interactive image that relates to the situation (optional).`}</li>
      <li parentName="ol"><strong parentName="li">{`Title:`}</strong>{` A short and concise explanation. Where possible, write this as a positive statement. In this example, “Start by adding data assets” feels more positive than “You don’t have any data assets.” Alternatively, you could say “You don’t have any data assets yet”.`}</li>
      <li parentName="ol"><strong parentName="li">{`Body:`}</strong>{` Explain clearly the next action to populate the space. You may also explain why the space is empty and include the benefit of taking this step. There are three options for explaining the primary action:`}<ul parentName="li">
          <li parentName="ul">{`Direct the user to a primary action button positioned underneath the copy`}</li>
          <li parentName="ul">{`Include a primary action link in the copy`}</li>
          <li parentName="ul">{`Direct the user to the UI element—see the example below. This has the benefit of teaching the user where elements are and how they will perform tasks in the future.`}</li>
        </ul></li>
      <li parentName="ol"><strong parentName="li">{`Primary action—button or link in copy (optional):`}</strong>{` The primary call to action referenced in the body copy above.`}</li>
      <li parentName="ol"><strong parentName="li">{`Secondary call to action (optional):`}</strong>{` If there is a secondary action, such as referencing documentation for further reading, include it as a link below the copy.`}</li>
    </ol>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABWUlEQVQ4y62Ty1KDMBSGeVKk1bGOSzd9Fdo6062LPoA7dy7c+ACOM0opkEq5qMglCb85oVZqbaedaWY+AsnhOychGDhyM4QQmEwmsG0bo9HoIIbDBnswwHg8RhzHMKqqQr/fh2ma6Ha7sCxrbzodC70zC+enJi4vepjNPBhUJkkZY0jTFFJKUNW74FyotwSmocTVDXByDdw/kaluhEmSwHEclWGGIAjg+/7/tOZY4ONlGuDucY7bB4ZnN1ImCaOua+R5vtpUet5A596ca0al7nmZq8r5utB1XURRpGPqSjQICZ5k4OH7KuFvchVaN1pyiL9Cz/MQLSLIvAL/KjS12i8efyrhx9YVYClcq5D6MAy1dK4+EAsYFuEClQqqWIri9a0Ryj2F1Mqy1PdEURSNgC5yubYdjeJXwizLtIwG2tBxakMx28bIQUdKC38mKEsbGjsEchnH/pe/AWSixKxGiryPAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Basic empty state with instruction to click UI element",
            "title": "Basic empty state with instruction to click UI element",
            "src": "/static/7c9f2f5feaeb08b1fad868e64b07ace6/3cbba/2_Empty_state_annotation_datatable_website_image_1120.png",
            "srcSet": ["/static/7c9f2f5feaeb08b1fad868e64b07ace6/7fc1e/2_Empty_state_annotation_datatable_website_image_1120.png 288w", "/static/7c9f2f5feaeb08b1fad868e64b07ace6/a5df1/2_Empty_state_annotation_datatable_website_image_1120.png 576w", "/static/7c9f2f5feaeb08b1fad868e64b07ace6/3cbba/2_Empty_state_annotation_datatable_website_image_1120.png 1152w", "/static/7c9f2f5feaeb08b1fad868e64b07ace6/0b124/2_Empty_state_annotation_datatable_website_image_1120.png 1728w", "/static/7c9f2f5feaeb08b1fad868e64b07ace6/99571/2_Empty_state_annotation_datatable_website_image_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Basic empty state with instruction to click UI element</Caption>
      </Column>
    </Row>
    <h2>{`Designing with empty states`}</h2>
    <p>{`Empty states are often treated as an afterthought. When designed thoughtfully, they become an essential part of a smooth user experience, providing just enough information, in context, to allow users to continue working in a productive way.`}</p>
    <p>{`During the design process, ask yourself these questions:`}</p>
    <ul>
      <li parentName="ul">{`What will the pages, tiles, data tables, and side panels look like without content?`}</li>
      <li parentName="ul">{`What are all of the steps a user can take to address the situation?`}</li>
      <li parentName="ul">{`Is there any useful content that might be available to show?`}</li>
      <li parentName="ul">{`How can I turn this situation into something that is engaging and helpful?`}</li>
    </ul>
    <p>{`During the design phase, explore the full range of options with your team to ensure that the most appropriate and helpful content is created for each empty space.`}</p>
    <h3>{`When to use`}</h3>
    <p>{`Empty states happen for a variety of reasons, and can require different treatments.`}</p>
    <p>{`Strive for a balance between the situation and the content you’re providing. More content doesn’t necessarily mean it’s a better solution as there is a cognitive cost for having more content on the page. This is especially true when users first engage with your product, so save the more involved educational moments for primary features and more complex situations.`}</p>
    <p>{`The following table suggests different approaches for empty states to match the needs of the user in different situations.`}</p>
    <Title mdxType="Title"> Basic empty states </Title>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListHead mdxType="StructuredListHead">
        <StructuredListRow head mdxType="StructuredListRow">
          <StructuredListCell head mdxType="StructuredListCell">Type</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Use cases</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Goal of the empty state</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">When to use</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">No data empty states</StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">First time use, no data yet</StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            User understands what will be available on the page when data has
            been added or is available. They understand how to add data
            themselves.
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            For simpler situations, or for secondary features where bite-sized
            pieces of information are preferable.
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">User action empty states</StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            Provides feedback based on some user action. For example:
            <UnorderedList mdxType="UnorderedList">
              <ListItem mdxType="ListItem">No results when searching</ListItem>
              <ListItem mdxType="ListItem">Confirmation of completion of a process</ListItem>
            </UnorderedList>
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            User understands how to adjust search terms or filters to continue
            their search. <br />
            <br />
            User understands that they've successfully completed a process.
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            When you need to provide feedback to the user based on an
            interaction.
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">Error management empty states</StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <UnorderedList mdxType="UnorderedList">
              <ListItem mdxType="ListItem">Permissions issue</ListItem>
              <ListItem mdxType="ListItem">Systems issue</ListItem>
              <ListItem mdxType="ListItem">Configuration required</ListItem>
            </UnorderedList>
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            User understands the problem and if there are corrective actions
            available, knows what action to take or has options to correct the
            issue.
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            When something is amiss or some level of intervention or
            troubleshooting is required, a higher level of detail and
            specificity will better support the user.
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3>{`Where to use`}</h3>
    <p>{`Empty states always appear in the otherwise empty space, in the context of the data that’s missing. They can occur anywhere your app can display data, including but not limited to dashboards, data tables, tiles, full pages, and side panels.`}</p>
    <p>{`The approach and layout you choose will depend on the situation, and what is the most appropriate based on the page layout and context.`}</p>
    <h3>{`Visual guidelines for smaller empty spaces`}</h3>
    <p>{`For small tiles and side panels, follow these guidelines and use the layouts shown below.`}</p>
    <h4>{`Alignment of elements`}</h4>
    <p>{`Empty state elements should be left-aligned as a block. The one exception to this rule is an empty state in a small tile. In this case the image should be centered above the left-aligned text and primary action, as shown in the example below. This exception was made to prevent the empty state looking too much like content, where it could be skipped over. The centered image in a smaller space helps to draw attention to a state that may require user action.`}</p>
    <h4>{`Image choice considerations`}</h4>
    <ul>
      <li parentName="ul">{`The image choice should relate to the situation.`}</li>
      <li parentName="ul">{`The size of the space for the empty state should also guide the size of image. If space is limited, use just text.`}</li>
    </ul>
    <h4>{`Multiple empty states`}</h4>
    <p>{`In situations where there could be multiple empty states showing at once, we recommend using a tertiary button for the call to action. This avoids scenarios with multiple primary action buttons in the UI.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "60.05434782608695%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAYAAABiDJ37AAAACXBIWXMAAAsSAAALEgHS3X78AAAA80lEQVQoz62SW26DMBBFs2Y+YBGIDyRYAh8srSigIFXhYZubHEduXUTaSA3SiBn7zpmHfNq2Te/6YJ1wrLVq21ZlWaqua1VV9ZKhJadpGq3r6qEeaIxRURRKkkRZlilN05cMLTl5nmue52/gO8YODA90zvkKjH5ky7J4zTAM6vveT8RZuCeX+x/AsINn1cdxVNd1mqbJ6wGFj9xDIIn4/GPfOavz5aqP4arPia6MzB0YdIdA2mYUDAGGTyf4ywrE3c8eK+CMOzR/jrwfaR/jB8DTkakaA6gcA/bAX3fIHgDEu7PRjvZx6DCOv55N/AaD4D/xDecTinJhDiMZAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Layout for empty state in a small tile",
            "title": "Layout for empty state in a small tile",
            "src": "/static/3d4d81b64a311456a84209bf85efa8ad/3cbba/3_Empty_state_small_tile_website_image_736.png",
            "srcSet": ["/static/3d4d81b64a311456a84209bf85efa8ad/7fc1e/3_Empty_state_small_tile_website_image_736.png 288w", "/static/3d4d81b64a311456a84209bf85efa8ad/a5df1/3_Empty_state_small_tile_website_image_736.png 576w", "/static/3d4d81b64a311456a84209bf85efa8ad/3cbba/3_Empty_state_small_tile_website_image_736.png 1152w", "/static/3d4d81b64a311456a84209bf85efa8ad/392b1/3_Empty_state_small_tile_website_image_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Layout for empty state in a small tile</Caption>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "60.05434782608695%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAYAAABiDJ37AAAACXBIWXMAAAsSAAALEgHS3X78AAABBElEQVQoz62STW6DMBCFc1QWgBCXQFGvABKcACkqJ+gyN+iid2ghJEhA/MtrxolTSqTgRSw94TH253kz3kzThFcNYm1oopRCVVXIsgxFUSDPc2elaYqyLCGEMFADpCBJEniehzAMEQTBqqIoQhzH8H3fnGWM/QFfZftuWWsNzrlZWJN1RKKYykWy/+4Zdl2Huq7RNM1T0Z7T6Yi2PaJuDrj2QD8Ch2FA3/dOGscR7eEHXftNp430HEiWbZfchsL+i2G7O+PtXeDj8wbV+F9D+q6J3MjL5YwrnC85DBxg4gqkJB+a4gIkN0pJ29sbbFFDKaVzhvPOztcNcP4G7fzZk1nuX8a/XTeDQNOothoAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Layout for empty state in a side panel",
            "title": "Layout for empty state in a side panel",
            "src": "/static/798d516360b64e0bcae3a9aa0027df29/3cbba/4_Empty_state_sidepanel_mock_website_image_736.png",
            "srcSet": ["/static/798d516360b64e0bcae3a9aa0027df29/7fc1e/4_Empty_state_sidepanel_mock_website_image_736.png 288w", "/static/798d516360b64e0bcae3a9aa0027df29/a5df1/4_Empty_state_sidepanel_mock_website_image_736.png 576w", "/static/798d516360b64e0bcae3a9aa0027df29/3cbba/4_Empty_state_sidepanel_mock_website_image_736.png 1152w", "/static/798d516360b64e0bcae3a9aa0027df29/392b1/4_Empty_state_sidepanel_mock_website_image_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Layout for empty state in a side panel</Caption>
      </Column>
    </Row>
    <h3>{`Visual guidelines for larger empty spaces`}</h3>
    <p>{`For larger spaces, you have some flexibility with laying out the larger empty states. Follow these guidelines, review the examples, and use your judgment as to what is best for the situation.`}</p>
    <h4>{`Alignment of elements`}</h4>
    <p>{`Empty state elements should be left-aligned as a block. Depending on the image you’re using, there are different arrangements available.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "62.90760869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABPElEQVQ4y41Ta1ODMBDk//8rx4/WkanWaQXKI4ClFahTaHhIVgLSEAjqzRwQbtl77KEBDIy11/bSNE13Hzs3ce5O6L8RsbFpAqS2KTm9MFw/BemUWOOBcwgkhxJ724TruqjrGlVVdRWPSTk22ACePk96q5CDnEeAvBYtoQHDMOF5HpIkURAC78ERL+ttG4+7pNylCofMSxmnLX/EJ1j7vpMwDEEplXDaX7NTDV6VdCQKV3fwRvKBVH4WrtoMZctLKgssk9ZHIuQvYw84+QV2b1tYpoWiKLrZyKL068Wx9gPgPwNf1UAKWeXdfQvSS5DAgW07IIQgTVO1yhbF+u4MsqlnM7yJYq8YIut/i+0HBE/6CrZrIYoOyLJsvth12ZZfq2cyrZDvHb1SZJcMeZ7PsJqQ/CeA3/5lttjFYN8VNvyBNv+NQwAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of layout types",
            "title": "Example of layout types",
            "src": "/static/d69c0fc159621eb643203ae99bc71364/3cbba/5_Layout_types_website_image_736.png",
            "srcSet": ["/static/d69c0fc159621eb643203ae99bc71364/7fc1e/5_Layout_types_website_image_736.png 288w", "/static/d69c0fc159621eb643203ae99bc71364/a5df1/5_Layout_types_website_image_736.png 576w", "/static/d69c0fc159621eb643203ae99bc71364/3cbba/5_Layout_types_website_image_736.png 1152w", "/static/d69c0fc159621eb643203ae99bc71364/392b1/5_Layout_types_website_image_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Layout/positioning options`}</h4>
    <p>{`You have two options for positioning the block in the larger empty states:`}</p>
    <ul>
      <li parentName="ul">{`Use a wider left margin`}</li>
      <li parentName="ul">{`Block center the left-aligned group in the empty space`}</li>
    </ul>
    <h4>{`Image position options`}</h4>
    <p>{`You have two options for positioning the image in the larger empty states:`}</p>
    <ul>
      <li parentName="ul">{`Above the empty state title—generally better for wider images`}</li>
      <li parentName="ul">{`To the left of the element block—generally better for taller images`}</li>
    </ul>
    <h4>{`Image choice considerations`}</h4>
    <ul>
      <li parentName="ul">{`The image choice should relate to the situation.`}</li>
      <li parentName="ul">{`The size of the space for the empty state should also guide the size of image. If space is limited, use just text.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAwklEQVQoz42RWw6CMBBF2f9OQCLshvhh+jDGhJ8mEEHp49opFkEUbXKbmfb0dqZNnHP4Nay1+IcjJqFASoldnqMoikllWSJNUzDGpgN1XSOfcfsnl2UZqqoKTDBs2xaCHXGSAkKMokvIrGmaybDrOnDOIfyeFMzzPHC0ppR6GVKph/OAwX5vJSqOi7Je7nPLxhhce4NBm/BelM8VzWJurcHtbtDfx5jWtNbLCv1MwaKaLQUeblV9EoOtT3xvF9HOrbkHEm0akaCmS5kAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "/images/Untitled.png",
            "title": "/images/Untitled.png",
            "src": "/static/8c3f8cb29663440cb9512e1937dd90eb/3cbba/6_large_tile_blocked_out_website_image_736.png",
            "srcSet": ["/static/8c3f8cb29663440cb9512e1937dd90eb/7fc1e/6_large_tile_blocked_out_website_image_736.png 288w", "/static/8c3f8cb29663440cb9512e1937dd90eb/a5df1/6_large_tile_blocked_out_website_image_736.png 576w", "/static/8c3f8cb29663440cb9512e1937dd90eb/3cbba/6_large_tile_blocked_out_website_image_736.png 1152w", "/static/8c3f8cb29663440cb9512e1937dd90eb/392b1/6_large_tile_blocked_out_website_image_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  Layout options for empty states in large tiles—wide margin or centered block
        </Caption>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAA10lEQVQoz42RzQ6CMBCEef9X0KuoIZT3gNgajT8JJl44GAwHLkAo7dgtFtDEnyYTltnpR7d4Wmv8WkopaKNfi1geFVmWIQgCMMYQksIQURRh4a+QppdhQ57nY85kbM7U/nKNw/FkMxZYFAWSJIEQApzzpwTmbIPz9T4Ay7JEHMe277K7LceMcezT2wiko3Zd92GMvu80He/lWnQftkCCVVWFtm3tfdE7SXXSPOUAI09KaTN1XaNpGlsr1fvDCR2EzKncBxyQavKoN82T5yb0/vnL7+N+yz0AdSsWaXBRCPYAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "/images/Untitled%201.png",
            "title": "/images/Untitled%201.png",
            "src": "/static/4afb71023bf3b86712df51c4c3c938b4/3cbba/7_data_table_blocked_out_website_image_736.png",
            "srcSet": ["/static/4afb71023bf3b86712df51c4c3c938b4/7fc1e/7_data_table_blocked_out_website_image_736.png 288w", "/static/4afb71023bf3b86712df51c4c3c938b4/a5df1/7_data_table_blocked_out_website_image_736.png 576w", "/static/4afb71023bf3b86712df51c4c3c938b4/3cbba/7_data_table_blocked_out_website_image_736.png 1152w", "/static/4afb71023bf3b86712df51c4c3c938b4/392b1/7_data_table_blocked_out_website_image_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  Layout options for empty states in tables—wide margin or centered block
        </Caption>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAA7ElEQVQoz41RQW6DMBDM/39COcBLKpUS7HKCHioVt46BRBgbPPWuCknVQ2Jp5PV4drzrPYQQcG+t64pHdKQ5UNA0DZLkCVmW7cjzPHIJ2rbdE7quQ5qm/3TECSFYw4Zaa5SvL5BSQEjJlzLuZVnCGLMbjuOIoihQVQL1m2SQrjoeoZS6Gi7Lgg814DJZuHnG/AtrLd9RKwRqnXjvHZQe8W0u8M6xznt/NbR2xnOt8fllMAw9TN+jj6DqyGD7H0o6Re489qjfTcSJY+KmafpbYVgdvzbfVEjYBnJbIfOLY2xn8mDDbXrhzvQenfIP1wIVWFn6QEYAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "/images/Untitled%202.png",
            "title": "/images/Untitled%202.png",
            "src": "/static/cdf30b8caeeabdbd60ab3b86e948dc0a/3cbba/8_full_page_blocked_out_website_image_736.png",
            "srcSet": ["/static/cdf30b8caeeabdbd60ab3b86e948dc0a/7fc1e/8_full_page_blocked_out_website_image_736.png 288w", "/static/cdf30b8caeeabdbd60ab3b86e948dc0a/a5df1/8_full_page_blocked_out_website_image_736.png 576w", "/static/cdf30b8caeeabdbd60ab3b86e948dc0a/3cbba/8_full_page_blocked_out_website_image_736.png 1152w", "/static/cdf30b8caeeabdbd60ab3b86e948dc0a/392b1/8_full_page_blocked_out_website_image_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  Layout options for full page empty states—wide margin and centered block
        </Caption>
      </Column>
    </Row>
    <h4>{`Best practices`}</h4>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Empty states should replace the element that would ordinarily show`}</strong>{`. For example, an empty state for a table would replace the table and the column headers and footer should not be present. This practice avoids having a screen reader read the entire table before getting to the message that there is no content in the table. Likewise, if you search for something and there are no results, any underlying content should be replaced by the empty state message.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Consider how many empty states may appear in a space`}</strong>{`. If you have a dashboard with a number of widgets and there is a failure for multiple widgets to load, the repetition of the empty state may not have the same impact if you use illustrative icons. In this case, an empty state that uses just text may be preferable.`}</p>
      </li>
    </ul>
    <h2>{`Types of empty states`}</h2>
    <h3>{`No data empty states`}</h3>
    <p>{`These are the most recognizable first use empty states, explaining what will be in the space once it is populated with data, and providing guidance for the next step the user can take to populate the space.`}</p>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABHklEQVQ4y62T3UqEQBiGvVRR7EC6BS9C8MAOUsGjoIuIiG6gOlh2KwnMgtafcWZ0TX2bmWq3o9BF4cEDv+/x9VU1LHxoXdfBcRzoug7LsmCaJgzDmIycl7u2bSPLMmjDMCCOY7iuC8/z4Ps+oihCGIaTkLNBEChHWZbQFn/kcRxRVZWyy3NRFMjzfDZyr+/7byFjDB/breqAUopaUtez2Qs550jTFOv1BoVIKu8oE8shQojiP9Hv9UNCIXx/e8Xz40qRPG2QJImq4O/CJGE/jBg7istbhtPzFidnO1zccHy2BGUl0s0VDiJhyylWLxTX9wxXdwwPCQWjcvDIDutaCiohJtgJGCWofrqbw17YNI140/yA6JQfgfxJFv+wvwA27rvy206+pAAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Basic empty state with primary action button",
            "title": "Basic empty state with primary action button",
            "src": "/static/4e762efdd44572d4843f50502c320496/3cbba/9_Empty_state_full_page_real_content_website_image_1120.png",
            "srcSet": ["/static/4e762efdd44572d4843f50502c320496/7fc1e/9_Empty_state_full_page_real_content_website_image_1120.png 288w", "/static/4e762efdd44572d4843f50502c320496/a5df1/9_Empty_state_full_page_real_content_website_image_1120.png 576w", "/static/4e762efdd44572d4843f50502c320496/3cbba/9_Empty_state_full_page_real_content_website_image_1120.png 1152w", "/static/4e762efdd44572d4843f50502c320496/0b124/9_Empty_state_full_page_real_content_website_image_1120.png 1728w", "/static/4e762efdd44572d4843f50502c320496/99571/9_Empty_state_full_page_real_content_website_image_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Basic empty state with primary action button</Caption>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABGUlEQVQ4y62Ty2qDQBSG50lFNCXppltfRSPUVXa+QBeluy5a6K6rUhAxNSp4A+/O/M1RQrGUMmkz8DEww3znnxvDhRsbxxGu68I0Tdi2fRbb7YxpWXAcB1mWgfV9D8MwoCgKNE2DqqrSrHQV65WKK13B9WaN/f4DjGKSNIoiFEUBzjko9W8MRwQfYT0ANztAvwUe38gkZmGe5/B9H0EQIAxDKQ6HEE+vEe5fYtw9R3gP0qOJgwkh0DTN4mBpTAZKRBLqh67BMAxLYVmWqOta+kbJycWsJcf4Xeh5HpIkmapTtTkFJJLOwkVC6tM0RRzH0wURVVX9XfjzloT09tu2/RJSkq7rpudzLqd15KAnNQlPE/+FXOzSf/kTqE/F5c0tPhsAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Basic empty state with instruction to click UI element",
            "title": "Basic empty state with instruction to click UI element",
            "src": "/static/e056dd229330d28837e8955918d71716/3cbba/10_Empty_state_data_table_real_content_website_image_1120.png",
            "srcSet": ["/static/e056dd229330d28837e8955918d71716/7fc1e/10_Empty_state_data_table_real_content_website_image_1120.png 288w", "/static/e056dd229330d28837e8955918d71716/a5df1/10_Empty_state_data_table_real_content_website_image_1120.png 576w", "/static/e056dd229330d28837e8955918d71716/3cbba/10_Empty_state_data_table_real_content_website_image_1120.png 1152w", "/static/e056dd229330d28837e8955918d71716/0b124/10_Empty_state_data_table_real_content_website_image_1120.png 1728w", "/static/e056dd229330d28837e8955918d71716/99571/10_Empty_state_data_table_real_content_website_image_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Basic empty state with instruction to click UI element</Caption>
      </Column>
    </Row>
    <h4>{`Do:`}</h4>
    <ul>
      <li parentName="ul">{`Use basic empty states for simpler situations, or secondary features, where bite-sized pieces of information are preferable.`}</li>
      <li parentName="ul">{`Be specific about what will be available in the space when data is there.`}</li>
      <li parentName="ul">{`Keep words to a minimum so they are fast to read and act upon.`}</li>
      <li parentName="ul">{`If there is an actionable next step, include a direct link in your message copy or a primary action button to make that action fast. Alternatively, guide them to what they need to click.`}</li>
    </ul>
    <h4>{`Don’t:`}</h4>
    <ul>
      <li parentName="ul">{`Don’t cover multiple options in one empty state. If there are multiple things a user can do, pick the most important and keep the focus on that action.`}</li>
      <li parentName="ul">{`Don’t use product-specific terms that the user may not yet understand.`}</li>
      <li parentName="ul">{`Don’t include content that is about other areas of the app. Be contextual.`}</li>
      <li parentName="ul">{`As a general rule, don’t lead the user into a dead end. If there are useful next steps, include them.`}</li>
    </ul>
    <h3>{`User action empty states`}</h3>
    <p>{`These empty states occur as a result of a user action. For example:`}</p>
    <ul>
      <li parentName="ul">{`A message showing there are no search results`}</li>
      <li parentName="ul">{`A success confirmation for completion of a process or set of tasks`}</li>
    </ul>
    <p>{`Explain the reason for seeing the message and providing any follow up steps to guide the user. For example, if there are no search results suggest adjusting the search or filters. Links to documentation may also be appropriate as a secondary call to action.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "60.05434782608695%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAYAAABiDJ37AAAACXBIWXMAAAsSAAALEgHS3X78AAAAx0lEQVQoz7WSWwqDMBBFXYmgq3B54kc2WVp/FJqPYDXGGIreJmMtWvtAbQM3ZCBzuPPw+r7HL4/nrq7rwDlHlmW7RcCyLBFFEXzfRxiGCIJgswjYNA0YY4jjGEmS7JL3lx46aNu2kFJCaz2TUmqVHsDRaV3XBK4qSb11A9vkcEzk/IzT8YA0TUl5nkMIgaIoqIqpgVdaOLxIDS6cS2WHpcilMWb251PfF0B9tWUbu5tvEr4NcQG00aCnUqawVQ6HN/Hu8bq1uQF272do31s2fQAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "A message showing there are no search results",
            "title": "A message showing there are no search results",
            "src": "/static/f55428451eb69d9778cdac14d4c63c41/3cbba/11_Empty_state_search_no_results_website_image_736.png",
            "srcSet": ["/static/f55428451eb69d9778cdac14d4c63c41/7fc1e/11_Empty_state_search_no_results_website_image_736.png 288w", "/static/f55428451eb69d9778cdac14d4c63c41/a5df1/11_Empty_state_search_no_results_website_image_736.png 576w", "/static/f55428451eb69d9778cdac14d4c63c41/3cbba/11_Empty_state_search_no_results_website_image_736.png 1152w", "/static/f55428451eb69d9778cdac14d4c63c41/392b1/11_Empty_state_search_no_results_website_image_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">A message showing there are no search results</Caption>
      </Column>
    </Row>
    <p>{`There may be situations where next steps are not possible or supplementary text is not required, so use your judgment about what is useful to include. For example, if your user has configured alerts and nothing has been triggered, it’s not a case of alerts not being set up but that there is nothing that requires the user’s attention. In this case, supplementary text is not necessary.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "60.05434782608695%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAYAAABiDJ37AAAACXBIWXMAAAsSAAALEgHS3X78AAABJ0lEQVQoz6WRX0uDUBjGve4DCF1UjEEfIJbXRfWZmtuVIvhN+jASQt2sRoGE5k2xqeiZHc/77HiW/cOtyR748SLK7zy+R9NkABxLriRnkvMN1O8vJSfaplRVdSM/QhiGIo5jrCOKIszncwRB8OC6rmFZ1sBxnFPbtgcNSuh53oVhGLzX66Hf79MuKOHB4dGeObxOxmMTI3MoRiOTTPNf0IYSQvB9ASzq35aTOGFd6MdsZSVEpb+8vjPv7gm3988UvxV4nE7h+z4mk4naX5IkW6GEi/JDF4Kzuh+Juh+BMYY8z1EUBcqyhLy4rfhsCJ1zztI0Q5qmNJvN6oksy5SsS5RQymRDwdQOhSAJGoioE19C+cD+LP77Joi6N2yEtMqvU7tIW4XYIUvipvs4AngbHwAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Basic empty state where next steps are not required",
            "title": "Basic empty state where next steps are not required",
            "src": "/static/2b7857749c1ee4095c86ef95abb4cb8d/3cbba/12_Empty_state_tabs_website_image_736.png",
            "srcSet": ["/static/2b7857749c1ee4095c86ef95abb4cb8d/7fc1e/12_Empty_state_tabs_website_image_736.png 288w", "/static/2b7857749c1ee4095c86ef95abb4cb8d/a5df1/12_Empty_state_tabs_website_image_736.png 576w", "/static/2b7857749c1ee4095c86ef95abb4cb8d/3cbba/12_Empty_state_tabs_website_image_736.png 1152w", "/static/2b7857749c1ee4095c86ef95abb4cb8d/392b1/12_Empty_state_tabs_website_image_736.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Basic empty state where next steps are not required</Caption>
      </Column>
    </Row>
    <h3>{`Error management empty states`}</h3>
    <p>{`Empty states can also occur when there is data but it cannot be surfaced for some reason. A higher level of specificity is helpful here, with the aim that the user will be able to address the issue comfortably themselves.`}</p>
    <p>{`Provide guidance about why there is no data, and either what the user can do to address the lack of data, or the circumstances under which the data would appear. And use plain language. Following this fulfills Jakob Nielsen’s ninth usability heuristic: `}<em parentName="p">{`Help users recognize, diagnose, and recover from errors`}</em>{`.`}</p>
    <blockquote>
      <p parentName="blockquote">{`“Error messages should be expressed in plain language (no codes), precisely indicate the problem, and constructively suggest a solution.”`}</p>
      <cite>
  - 10 Usability Heuristics for User Interface Design, Jakob Nielsen
      </cite>
    </blockquote>
    <p>{`Here are some error management situations that may be addressed with the basic empty state solution.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Error type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Explain why there is no data`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Explain what the user can do`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Permissions issue`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The user does not have permission to view the data.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Suggest steps or process to request access.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Systems issue`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Problems with a related system are preventing the data from being supplied.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Explain steps the user can take to learn what has happened. For example, viewing an activity log.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Configuration required`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Further configuration may be needed to access the data.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provide an explanation and the first step for the user to take for the required configuration.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Action not supported`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`For example, the user attempts to upload an unsupported file type.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Explain what file types are supported.`}</td>
        </tr>
      </tbody>
    </table>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABNElEQVQ4y62TvW6DMBSFeVQEysLWB+AhkDIwQSSeIFM7da86dKq6JLQNVIj+AP4BUgScxiAQpDRlwNInY/vcc32vjISFh1QUBXRdhyzLWK1WUFUViqLMRuhFrKZpCIIAUlVVcBwHhmFgvV7DNE1sNhvYtj0LobUsq/FIkgTS4iXXdQ1CCOI47omiqEFkFHTrS4i4sixbQ845KKVgjPWIPc/zEIYh0jRtzjuGuiGThsMg9/kVnh8gIe1aGAttp+uMuu8/DduZ4P1th/DwiJf9E/aue7rxAb7vN20Yay8aiqwUccJg3nBc2Smu7ykyFuHj86vp1+/k/xqyU5kM27sc9m2Oh12KYy72eV/yrB6eUx4JUBJ8Z+T0EuikZqTvDPM8bxqeZdkInmZgvJ3Pz6YQP8niD/sH0U68I8Y48+UAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of an error management empty state for a systems issue",
            "title": "Example of an error management empty state for a systems issue",
            "src": "/static/fe1641b0556eb6e579b02171e8bb6e8c/3cbba/13_Empty_state_system_error_website_image_1120.png",
            "srcSet": ["/static/fe1641b0556eb6e579b02171e8bb6e8c/7fc1e/13_Empty_state_system_error_website_image_1120.png 288w", "/static/fe1641b0556eb6e579b02171e8bb6e8c/a5df1/13_Empty_state_system_error_website_image_1120.png 576w", "/static/fe1641b0556eb6e579b02171e8bb6e8c/3cbba/13_Empty_state_system_error_website_image_1120.png 1152w", "/static/fe1641b0556eb6e579b02171e8bb6e8c/0b124/13_Empty_state_system_error_website_image_1120.png 1728w", "/static/fe1641b0556eb6e579b02171e8bb6e8c/99571/13_Empty_state_system_error_website_image_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  Example of an error management empty state for a systems issue
        </Caption>
      </Column>
    </Row>
    <h4>{`Do:`}</h4>
    <ul>
      <li parentName="ul">{`Consult with your team to explore what information is available.`}</li>
      <li parentName="ul">{`If there are corrective actions, give the user an actionable next step. Include a direct link in your message copy or a primary action button to make the next action fast. Alternatively, guide them to the element they need to click.`}</li>
      <li parentName="ul">{`Use direct, plain language to describe the situation.`}</li>
      <li parentName="ul">{`Be respectful of the user and don’t joke or use flippant language.`}</li>
      <li parentName="ul">{`Any images used should reflect the situation and be sensitive to what could be a serious situation.`}</li>
    </ul>
    <h4>{`Don’t:`}</h4>
    <ul>
      <li parentName="ul">{`Don’t include content that is about other areas of the app. Be contextual.`}</li>
      <li parentName="ul">{`Don’t lead the user into a dead end. Always aim to provide a path to a solution.`}</li>
      <li parentName="ul">{`If there are multiple things for the user to try, be sure to include a hierarchy so that it’s clear which option would be the primary action.`}</li>
    </ul>
    <p>{`For more detailed information about writing error messages, see the Nielsen Norman group’s `}<a parentName="p" {...{
        "href": "https://www.nngroup.com/articles/error-message-guidelines/"
      }}>{`Error Message Guidelines`}</a>{`.`}</p>
    <p>{`A pattern providing guidance for error states is currently being planned. If you would like to contribute, please see our `}<a parentName="p" {...{
        "href": "/how-to-contribute/pattern"
      }}>{`guidelines for contributions`}</a>{`.`}</p>
    <h2>{`In-depth alternatives`}</h2>
    <p>{`Educational content that helps users get oriented and started is another option for turning an otherwise empty space into a positive and productive experience for first time users.`}</p>
    <p>{`When deciding on approaches, a good rule of thumb is that a primary resource on a page could benefit from a more educational approach, while basic empty states may suffice for secondary resources.`}</p>
    <Title mdxType="Title"> Alternative approaches </Title>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListHead mdxType="StructuredListHead">
        <StructuredListRow head mdxType="StructuredListRow">
          <StructuredListCell head mdxType="StructuredListCell">Type</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Use cases</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Goal of the empty state</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">When to use</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">In-line documentation</StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <UnorderedList mdxType="UnorderedList">
              <ListItem mdxType="ListItem">First time use (of app or feature)</ListItem>
              <ListItem mdxType="ListItem">Cleared or deleted data</ListItem>
              <ListItem mdxType="ListItem">Configuration required</ListItem>
            </UnorderedList>
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            Detailed overview of page so the user understands more about the
            feature and is given at least one call to action to get started.
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            When introducing a primary feature, there is an opportunity to
            showcase the benefit of usage. More educational detail could help to
            drive engagement.
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">Onboarding</StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">First time use</StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            Starting from an empty state, users have an opportunity to launch a
            contextual onboarding flow to gain deeper understanding about that
            area of the app.
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            When introducing a primary feature that may require more detailed
            explanation of concepts, a step-through of a workflow sequence, or
            other information that enhances productivity. <br />
            <br />
            Usually onboarding flows are optional for users, therefore they need
            to be used in conjunction with basic empty states.
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">Starter content</StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">First time use</StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            User can interact with data and learn the system by tinkering.{' '}
            <br />
            <br />
            User gets a jumpstart to productivity with basic setup tasks being
            taken care of for them.
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            For complex situations, starter content can be a good choice as it
            saves users a lot of time both in learning and in configuring
            setups.
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3>{`In-line documentation`}</h3>
    <p>{`In-line documentation is an extension of the basic empty state for first time use. It can be most helpful when a primary feature is first introduced, providing more detail and highlighting any benefits. Including an image of the space populated with data may help trigger interest and usage. Following a progressive disclosure model, it could provide links out to more detailed documentation.`}</p>
    <h4>{`Considerations for in-line documentation`}</h4>
    <ul>
      <li parentName="ul">{`If testing results show that users do not understand the feature or concept, more detail may encourage usage.`}</li>
      <li parentName="ul">{`This approach may require a higher level of maintenance than a basic empty state if using product images as images will need to be kept up to date. If your app is translated, there could be extra work in providing localized images.`}</li>
      <li parentName="ul">{`Keep the content limited to one feature. Do not talk about other areas of the app. If there are multiple things a user could do, pick the most important and keep the focus on that.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABZUlEQVQ4y61STUvDQBDNz/EXeS1pb/0LBf+BPXgoTaHH3FUoSMGDJw+Cx94L/TA1NUnTbHaT3Tw72WZNUCxiBx4z+3bnZd5uLJw5rCzL0Ov10Gq10Ol00G63/wTqsW0b3W4X6/UaVp7ncF0Xg8EAjuOcxGg0Mqi44XCI8XiMIAhgFUVxNrukZSmlwBiDlBI0LSHLJbjQ64qnHMcxfN83oD66Mtqjms6Vgpxz8wUdilaGq3iyNJ/PsVwu4XlemUmYQghRChvBqoknEe6eN+hPFVKhOXXc2+32WKzesHnfIoxiRFGEMAy1q+OkDUFqU1mEK2eLi8sVvK2eXCma8lDIBMgCID+IsI/fBeuWRSrhe3FpmyipirJ+muW4nnD0JwK3L6LxIN8s11/K1MdcCT7OgP4DcDMF7l/1ieo6frzDOrTN5lp/4guq9mBGkIg0TUvRU2ApR8I49kzn+h5p0HBW/df4Lyg+AQ48w1ERMYgbAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of an in-line empty state",
            "title": "Example of an in-line empty state",
            "src": "/static/a6a04ba16daec2ffca9ec20ead5ddb90/3cbba/14_Empty_state_inline_doc_website_image_1120.png",
            "srcSet": ["/static/a6a04ba16daec2ffca9ec20ead5ddb90/7fc1e/14_Empty_state_inline_doc_website_image_1120.png 288w", "/static/a6a04ba16daec2ffca9ec20ead5ddb90/a5df1/14_Empty_state_inline_doc_website_image_1120.png 576w", "/static/a6a04ba16daec2ffca9ec20ead5ddb90/3cbba/14_Empty_state_inline_doc_website_image_1120.png 1152w", "/static/a6a04ba16daec2ffca9ec20ead5ddb90/0b124/14_Empty_state_inline_doc_website_image_1120.png 1728w", "/static/a6a04ba16daec2ffca9ec20ead5ddb90/99571/14_Empty_state_inline_doc_website_image_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3>{`Onboarding`}</h3>
    <p>{`Onboarding services provide various options for contextual in-app guidance that can enhance or direct a user through an empty state.`}</p>
    <p>{`Stepping users through workflows with information that supplements the empty state messages can get the user to a place of comfort quickly. Tours can point out the location of features, provide more detail for key tasks, and highlight benefits. Because onboarding flows are optional for users, they need to be used in conjunction with basic empty states.`}</p>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAAB5ElEQVQ4y62TzasSYRTG599qZRtXIrgIVBBBESUQvRGajfP9PeYHhbUTHAXvJlpcIuoWXAgKWrQuIYjpXsmbFrNQjFw8zfvKmN5Fbu7AjxfOOe9znnNehsE1f8x6vYaiKMhmsygUCsjn8wfJZDJIp9Mol8solUrI5XKoVCqYTqdglsslQqEQwuEwotEoIpHIfyE1xWIRR0d3qBFZUSGIEhV2XRfMYrFAKpWCZVnodDpotVpot9t7BLFms4lut4vx+DMuLs7xzf2KhTelo755fYrJZLIRjMfj4DgOqqrSrlcJ4pIkwTRNjL+4+OktcPnrN158/IOzT8Dx0+e+4PlGMJFIQBAE6LpOL2uatj1lWUa9Xt/GDMPAs5NXOD17j5OXb3Hv0Tuwjz/g4RMHPy6/HxYkcbJwIkxiBEkU/DjnnzweGDwslUXDNjGfzw8Liv7larUKlr0Pnufp6KSOoJF6zfAfRoNtNw4JarAMFbfv6rhxy8LNhAmW12DomxypCSBNbNv+J5hMJn0nIt1P0J1g+EiyjmpdR40jbvS9fAAx0WjsOIzFYqjVavQVidNdyMiKJED2Ea/kNnmRroI4nc1mYFarFbXc6/UwHA7hOA4Gg8EWx9lnNxfQ7/cxGo3geR6Y6/6X/wJpmuoTt9t+WQAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of onboarding guidance",
            "title": "Example of onboarding guidance",
            "src": "/static/8accdeb6dddf29bae4d75c668bd860c3/3cbba/15_Empty_state_onboarding_website_image_1120.png",
            "srcSet": ["/static/8accdeb6dddf29bae4d75c668bd860c3/7fc1e/15_Empty_state_onboarding_website_image_1120.png 288w", "/static/8accdeb6dddf29bae4d75c668bd860c3/a5df1/15_Empty_state_onboarding_website_image_1120.png 576w", "/static/8accdeb6dddf29bae4d75c668bd860c3/3cbba/15_Empty_state_onboarding_website_image_1120.png 1152w", "/static/8accdeb6dddf29bae4d75c668bd860c3/0b124/15_Empty_state_onboarding_website_image_1120.png 1728w", "/static/8accdeb6dddf29bae4d75c668bd860c3/99571/15_Empty_state_onboarding_website_image_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Considerations for onboarding`}</h4>
    <ul>
      <li parentName="ul">{`If metrics are showing that users are not attempting to use a feature, even with an empty state, you may need supplementary information. An empty state can be used as a launching point for an onboarding sequence, to help users get through a process.`}</li>
      <li parentName="ul">{`When using an onboarding third party service, metrics can be viewed at any time, and adjustments to content can be made and deployed immediately.`}</li>
      <li parentName="ul">{`Usually onboarding flows are optional for users, therefore they need to be used in conjunction with basic empty states.`}</li>
      <li parentName="ul">{`Another aspect to consider is maintenance and the level of commitment required to maintain it.`}</li>
    </ul>
    <p>{`A pattern providing guidance for onboarding is currently being planned. It will include a framework for matching user needs with design solutions, tips for writing copy, and general best practices. If you would like to contribute, please see our `}<a parentName="p" {...{
        "href": "/how-to-contribute/pattern"
      }}>{`guidelines for contributions`}</a>{`.`}</p>
    <h3>{`Starter content`}</h3>
    <p>{`Another strategy for addressing an otherwise empty space is to provide pre-built content that allows new users to get started with an app quickly and without any concern.`}</p>
    <h4>{`Pre-built content for apps`}</h4>
    <p>{`Starter content can provide the opportunity for users to dive in and learn about primary features and functions with sample data. Users can tinker, examining and deleting content without serious consequences. If it’s possible to include some personalization here, that adds to the positive experience.`}</p>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABJElEQVQ4y62TTUrEQBCFc9OQZJcz5AJuBLeBJIsshCC4EK8gihdwIS4Sx8wgGPPT+el08uxqJ2HAGXWYNHwNXdX1uupBa1h4aZxzOI4DXddhWRZM0zwKqjEMA7ZtI01TaEIIRFEE13URBMEPfN9X7MtNeJ6HMAyRZRm0qdVhXGhk2lr2iWzzguojActekcQx4jiRxGqMtm1Bk/R9/yvjOH4LPj4LnF23OL/pcHHboWSdvMBB/nZdh2EY1OtUcGhRbis44mkFXN4B0T1w9QD04nDBX2gDmSdaWcIktYSj33a2C98T242TLTSJRqpFWWL1tsZ68468KFWS4nThv0w+K0HGGEopSuR5jqIo5vMxzIJVVc1CpzALNk2juqzr+iSUh0v/5S/bvsUwBViQ6wAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Creating a chatbot is easier when you have a starting place with pre-built content.",
            "title": "Creating a chatbot is easier when you have a starting place with pre-built content.",
            "src": "/static/e64a190dff6b9c9a58bad0a0bf589942/3cbba/16_Empty_state_prebuilt_content_website_image_736.png",
            "srcSet": ["/static/e64a190dff6b9c9a58bad0a0bf589942/7fc1e/16_Empty_state_prebuilt_content_website_image_736.png 288w", "/static/e64a190dff6b9c9a58bad0a0bf589942/a5df1/16_Empty_state_prebuilt_content_website_image_736.png 576w", "/static/e64a190dff6b9c9a58bad0a0bf589942/3cbba/16_Empty_state_prebuilt_content_website_image_736.png 1152w", "/static/e64a190dff6b9c9a58bad0a0bf589942/0b124/16_Empty_state_prebuilt_content_website_image_736.png 1728w", "/static/e64a190dff6b9c9a58bad0a0bf589942/99571/16_Empty_state_prebuilt_content_website_image_736.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  Creating a chatbot is easy when you have a starting place with pre-built
  content.
        </Caption>
      </Column>
    </Row>
    <blockquote>
      <p parentName="blockquote">{`“When someone feels like she can explore an interface and not suffer dire consequences, she’s likely to learn more—and feel more positive about it—than someone who doesn’t explore. Good software allows people to try something unfamiliar, back out, and try something else, all without stress.”`}</p>
      <cite>
  -Jenifer Tidwell, Designing Interfaces, (O'Reilly Media, 2011), 9.
      </cite>
    </blockquote>
    <h4>{`Pre-configured kits and workflows`}</h4>
    <p>{`Other options for starter content are pre-configured kits and workflows. Pre-configured kits containing code and API credentials can offer a fast path to getting started.`}</p>
    <p>{`Starter content can also take the form of pre-configured workflows, where the configuration of services is automated, saving users from the tedium of a long setup process.`}</p>
    <h4>{`Considerations for starter content`}</h4>
    <ul>
      <li parentName="ul">{`Requires upfront planning with the full product team to determine workflow pre-configurations that would most benefit users.`}</li>
      <li parentName="ul">{`If your starter content can be deleted by your user, you will need a basic empty state as a back up.`}</li>
    </ul>
    <h2>{`Accessibility`}</h2>
    <p>{`As most empty state illustrations are considered decorative, they should be skipped by screen readers.`}</p>
    <p>{`A decorative image is one that does not serve any practical or informational purpose, and is included to fill a visual void. Do not include any informational content in your decorative image.`}</p>
    <p><a parentName="p" {...{
        "href": "https://www.w3.org/WAI/tutorials/images/decorative/"
      }}>{`Web Content Accessibility Guidelines`}</a>{` require that decorative images are given either an empty `}<inlineCode parentName="p">{`alt`}</inlineCode>{` tag or their `}<inlineCode parentName="p">{`role`}</inlineCode>{` is assigned `}<inlineCode parentName="p">{`presentation`}</inlineCode>{`. As an empty `}<inlineCode parentName="p">{`alt`}</inlineCode>{` tag is more widely supported, we recommend you align with the WCAG guidance and avoid assigning `}<inlineCode parentName="p">{`role`}</inlineCode>{` to `}<inlineCode parentName="p">{`presentation`}</inlineCode>{` until support is more ubiquitous.`}</p>
    <h2>{`Related`}</h2>
    <Row mdxType="Row">
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4>{`Components`}</h4>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/button/usage"
            }}>{`Button`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/data-table/usage"
            }}>{`Data table`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/tile/usage"
            }}>{`Tile`}</a></li>
        </ul>
      </Column>
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4>{`Patterns`}</h4>
        <ul>
          <li parentName="ul">{`Error states (future)`}</li>
          <li parentName="ul">{`Onboarding (future)`}</li>
        </ul>
      </Column>
    </Row>
    <h2>{`References`}</h2>
    <ul>
      <li parentName="ul">{`Jakob Nielsen, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/error-message-guidelines/"
        }}>{`Error Message Guidelines`}</a>{` (Nielsen Norman Group, 2001)`}</li>
      <li parentName="ul">{`Jakob Nielsen and Page Labuheimer, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/top-10-application-design-mistakes/"
        }}>{`Top 10 Application-Design Mistakes`}</a>{` (Nielsen Norman Group, 2019)`}</li>
      <li parentName="ul">{`Jenifer Tidwell, Designing Interfaces (O’Reilly Media, 2nd edition, 2011)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/standards-guidelines/wcag/"
        }}>{`Web Content Accessibility Guidelines`}</a>{` (W3C, 2018)`}</li>
      <li parentName="ul">{`Kathryn Whitenton, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/search-no-results-serp/"
        }}>{`3 Guidelines for Search Engine “No Results” Pages`}</a>{` (Nielsen Norman Group, 2014)`}</li>
    </ul>
    <h2>{`Feedback`}</h2>
    <p>{`Help us improve this pattern by providing feedback, asking questions, and leaving any other comments on `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-website/issues/new?assignees=&labels=feedback&template=feedback.md"
      }}>{`GitHub`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      